import * as Tooltip from '@radix-ui/react-tooltip';
import type { LucideIcon } from 'lucide-react';
import type * as React from 'react';
import './NavigationRail.scss';

interface Props {
  route: string;
  label: string;
  icon: LucideIcon;
  isActive: boolean;
  collapsed: boolean;
  showPendingDot?: boolean;
  onClick: () => void;
}

export function NavigationItem({
  route,
  label,
  icon: Icon,
  isActive,
  collapsed,
  showPendingDot = false,
  onClick,
}: Props): React.ReactElement {
  const className = [
    'NAVIGATION__ITEM',
    collapsed ? 'NAVIGATION__ITEM--collapsed' : 'NAVIGATION__ITEM--expanded',
    isActive ? 'NAVIGATION__ITEM--active' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const button = (
    <button
      type="button"
      className={className}
      onClick={onClick}
      aria-label={label}
      aria-current={isActive ? 'page' : undefined}
      data-route={route}
    >
      <span className="NAVIGATION__ITEM__ICON">
        <Icon size={20} aria-hidden="true" />
        {showPendingDot && (
          <span
            className="NAVIGATION__ITEM__PENDING_DOT"
            data-testid="nav-item-pending-dot"
            role="img"
            aria-label="pending action"
          />
        )}
      </span>
      {!collapsed && <span className="NAVIGATION__ITEM__LABEL">{label}</span>}
    </button>
  );

  // Always wrap in Tooltip.Root so the button DOM element stays mounted
  // across collapse-state toggles. Gate the Portal/Content render on
  // `collapsed` — Tooltip.Root is effectively a no-op when no Content is
  // rendered.
  //
  // TRIL-146 I7: key Tooltip.Root on the collapsed state to force a fresh
  // internal state (hover/focus tracking) on every transition. Without
  // this, a tooltip that was hover-armed during the expanded→collapsed
  // toggle would open immediately when the Portal mounts, making labels
  // appear stuck-open until the user moves the cursor.
  return (
    <Tooltip.Root key={collapsed ? 'c' : 'e'}>
      <Tooltip.Trigger asChild>{button}</Tooltip.Trigger>
      {collapsed && (
        <Tooltip.Portal>
          <Tooltip.Content
            side="right"
            sideOffset={8}
            className="NAVIGATION__ITEM__TOOLTIP"
          >
            {label}
          </Tooltip.Content>
        </Tooltip.Portal>
      )}
    </Tooltip.Root>
  );
}
